import { useChat } from "@ai-sdk/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DefaultChatTransport, type UIMessage } from "ai";
import { Card, Drawer, Form, message } from "antd";
import { parseAsString, useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "streamdown/styles.css";

import { ensureAgentChatSession, persistAgentChatMessage } from "@fitness-app/client-shared/features/sales-agent";
import {
  type AgentChatMessage,
  type AgentChatSession,
  type AgentSkill,
  TeamMemberRole,
} from "@fitness-app/data-models";
import { type TeamMemberEntity } from "@fitness-app/data-models/entities/TeamMember";
import { type PromptInputFile } from "@fitness-app/web-ui/components/ai-elements/prompt-input";

import { usePostHogFeatureFlag } from "~/hooks/usePostHogFeatureFlag";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { supabase } from "~/store/initializeStore";

import { SalesAgentChatCard } from "./SalesAgent/SalesAgentChatCard";
import { SalesAgentPanel } from "./SalesAgent/SalesAgentPanel";
import { SkillEditorDrawer } from "./SalesAgent/SkillEditorDrawer";
import {
  createSlug,
  defaultSalesAgentModelOptionId,
  getSalesAgentModelOption,
  defaultSkillContent,
  getClientWebApiBaseUrl,
  SALES_AGENT_WORKFLOW_TRANSPORT_FLAG,
  salesAgentChatType,
  sessionsQueryKey,
  skillsQueryKey,
  type SalesAgentModelOptionId,
  type SkillFormValues,
} from "./SalesAgent/constants";
import { copyTextToClipboard, getMessageText, mapStoredMessage } from "./SalesAgent/messageUtils";
import { clearActiveRun, loadActiveRun } from "./SalesAgent/workflowRunStore";
import { cancelSalesAgentWorkflowRun, createSalesAgentWorkflowTransport } from "./SalesAgent/workflowTransport";
import { useTranslation as useInterfaceTranslation } from "react-i18next";

const disableObservationalMemoryDebugStorageKey = "salesAgentDebugDisableObservationalMemory";

interface SalesAgentProps {
  canInspectSalesRepChats: boolean;
  canManageSkills: boolean;
}

/**
 * TRE-1911 — wybór transportu czatu za flagą PostHog. Transport jest przybijany
 * przy montowaniu instancji useChat, więc: czekamy z montażem czatu na załadowanie
 * flag (bez tego każdy start liczyłby się jako legacy), a `key` wymusza czysty
 * remount przy przełączeniu flagi w locie — kill-switch działa bez deploya.
 */
export const SalesAgent = (props: SalesAgentProps): React.ReactElement => {
  const { enabled: workflowTransportEnabled, isLoading } = usePostHogFeatureFlag(SALES_AGENT_WORKFLOW_TRANSPORT_FLAG);

  if (isLoading) {
    return <Card loading className="h-full rounded-lg border border-gray-200 shadow-sm" />;
  }

  return (
    <SalesAgentChat
      key={workflowTransportEnabled ? "workflow" : "legacy"}
      {...props}
      useWorkflowTransport={workflowTransportEnabled}
    />
  );
};

interface SalesAgentChatProps extends SalesAgentProps {
  useWorkflowTransport: boolean;
}

const SalesAgentChat = ({
  canInspectSalesRepChats,
  canManageSkills,
  useWorkflowTransport,
}: SalesAgentChatProps): React.ReactElement => {
  const { t: tUi } = useInterfaceTranslation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<SkillFormValues>();
  const { userId } = useUserRole();
  const [activeSkill, setActiveSkill] = useState<AgentSkill | null>(null);
  const [agentPanelOpen, setAgentPanelOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [activeSessionId, setActiveSessionId] = useQueryState("sessionId", parseAsString);
  const [modelQueryState, setModelQueryState] = useQueryState("model", parseAsString);
  const [selectedHistoryOwnerId, setSelectedHistoryOwnerId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const activeSessionIdRef = useRef<string | null>(null);
  const selectedModelIdRef = useRef<SalesAgentModelOptionId>(defaultSalesAgentModelOptionId);
  const userIdRef = useRef<string | null>(null);
  const previousHistoryOwnerIdRef = useRef<string | null>(null);
  const promptTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const skipHydrationSessionIdRef = useRef<string | null>(null);

  const historyOwnerId = canInspectSalesRepChats ? (selectedHistoryOwnerId ?? userId) : userId;
  const isViewingOwnChats = historyOwnerId === userId;
  const selectedModelId = getSalesAgentModelOption(modelQueryState).id;

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  useEffect(() => {
    selectedModelIdRef.current = selectedModelId;
  }, [selectedModelId]);

  useEffect(() => {
    userIdRef.current = userId ?? null;
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    setSelectedHistoryOwnerId((current) => current ?? userId);
  }, [userId]);

  const persistMessage = useCallback(async (sessionId: string, chatMessage: UIMessage) => {
    await persistAgentChatMessage(supabase, {
      sessionId,
      message: chatMessage,
      modelId: selectedModelIdRef.current,
    });
  }, []);

  const ensureSession = useCallback(
    async (
      firstMessageText: string,
      options: { optimistic?: boolean; sessionId?: string } = {},
    ): Promise<AgentChatSession> => {
      const session = await ensureAgentChatSession(tUi, supabase, {
        activeSessionId: activeSessionIdRef.current,
        firstMessageText,
        modelId: selectedModelIdRef.current,
        optimistic: options.optimistic,
        sessionId: options.sessionId,
      });

      if (session.id !== activeSessionIdRef.current) {
        activeSessionIdRef.current = session.id;
        void setActiveSessionId(session.id);
        await queryClient.invalidateQueries({ queryKey: sessionsQueryKey });
      }

      return session;
    },
    [tUi, queryClient, setActiveSessionId],
  );

  const chatTransport = useMemo(() => {
    if (useWorkflowTransport) {
      return createSalesAgentWorkflowTransport({
        getActiveSessionId: () => activeSessionIdRef.current,
        getUserId: () => userIdRef.current,
        getModelId: () => selectedModelIdRef.current,
        getDisableObservationalMemory: () =>
          window.localStorage.getItem(disableObservationalMemoryDebugStorageKey) === "true",
      });
    }

    return new DefaultChatTransport<UIMessage>({
      api: `${getClientWebApiBaseUrl()}/api/sales-agent/chat`,
      prepareSendMessagesRequest: async ({ api, body, headers, credentials, id, messages, trigger, messageId }) => {
        const token = (await supabase.auth.getSession()).data.session?.access_token;
        const latestMessage = messages.at(-1);
        const disableObservationalMemory =
          window.localStorage.getItem(disableObservationalMemoryDebugStorageKey) === "true";

        return {
          api,
          body: {
            ...body,
            id,
            sessionId: activeSessionIdRef.current ?? undefined,
            model: selectedModelIdRef.current,
            messages: latestMessage ? [latestMessage] : [],
            trigger,
            messageId,
            debug: disableObservationalMemory ? { disableObservationalMemory } : undefined,
          },
          credentials,
          headers: {
            ...headers,
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        };
      },
    });
  }, [useWorkflowTransport]);

  // Run zapisany przed odświeżeniem karty — jeśli żyje (TTL 10 min, ten sam
  // użytkownik), useChat wznowi stream zamiast pokazać uciętą odpowiedź.
  const initialRunRef = useRef<ReturnType<typeof loadActiveRun> | undefined>(undefined);
  if (initialRunRef.current === undefined) {
    initialRunRef.current = useWorkflowTransport ? loadActiveRun(userId ?? null) : null;
  }

  const {
    messages: chatMessages,
    clearError,
    regenerate,
    sendMessage,
    setMessages,
    status: chatStatus,
    stop,
    error: chatError,
  } = useChat<UIMessage>({
    transport: chatTransport,
    resume: Boolean(initialRunRef.current),
    experimental_throttle: 40,
    onFinish: async ({ message: assistantMessage }) => {
      const sessionId = activeSessionIdRef.current;
      if (!sessionId || assistantMessage.parts.length === 0) return;

      try {
        // Na ścieżce workflow odpowiedź persystuje SERWER (finalizeSalesAgentChat)
        // — zapis stąd zdublowałby wiadomość w sesji.
        if (!useWorkflowTransport) {
          await persistMessage(sessionId, assistantMessage);
        }
        skipHydrationSessionIdRef.current = null;
        await queryClient.invalidateQueries({ queryKey: sessionsQueryKey });
      } catch {
        // Message streaming succeeded, so do not interrupt the UI if persistence fails.
      } finally {
        window.requestAnimationFrame(() => promptTextareaRef.current?.focus());
      }
    },
  });

  // stop() ucina tylko lokalny stream — na ścieżce workflow run generowałby dalej
  // po stronie serwera, a marker activeRun kazałby wznawiać niechcianą odpowiedź.
  const stopChat = useCallback(() => {
    stop();

    if (!useWorkflowTransport) return;

    const activeRun = loadActiveRun(userIdRef.current);
    if (!activeRun) return;

    clearActiveRun();
    void cancelSalesAgentWorkflowRun(activeRun).catch(() => {
      // Run mógł już się skończyć — celem jest „nie generuje", nie sam status 200.
    });
  }, [stop, useWorkflowTransport]);
  const isChatBusy = chatStatus === "submitted" || chatStatus === "streaming";

  useEffect(() => {
    if (!historyOwnerId) return;

    if (!previousHistoryOwnerIdRef.current) {
      previousHistoryOwnerIdRef.current = historyOwnerId;
      return;
    }

    if (previousHistoryOwnerIdRef.current === historyOwnerId) return;

    previousHistoryOwnerIdRef.current = historyOwnerId;
    void setActiveSessionId(null);
    activeSessionIdRef.current = null;
    skipHydrationSessionIdRef.current = null;
    setMessages([]);
  }, [historyOwnerId, setActiveSessionId, setMessages]);

  const sessionsQuery = useQuery({
    queryKey: [...sessionsQueryKey, historyOwnerId],
    enabled: Boolean(historyOwnerId),
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_chat_session")
        .select("*")
        .eq("agentType", salesAgentChatType)
        .eq("ownerId", historyOwnerId)
        .order("lastMessageAt", { ascending: false })
        .limit(50);

      if (error) throw new Error(error.message);
      return (data ?? []) as AgentChatSession[];
    },
  });
  const isChatPersistenceUnavailable =
    sessionsQuery.isError && (sessionsQuery.error as Error).message.includes("agent_chat_session");

  const sellersQuery = useQuery({
    queryKey: ["salesAgentChatOwners"],
    enabled: canInspectSalesRepChats,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_member")
        .select("id, firstName, lastName, email, role, userId")
        .eq("role", TeamMemberRole.SALES_REP)
        .not("userId", "is", null)
        .order("firstName", { ascending: true });

      if (error) throw new Error(error.message);
      return (data ?? []) as Pick<TeamMemberEntity, "id" | "firstName" | "lastName" | "email" | "role" | "userId">[];
    },
  });

  const messagesQuery = useQuery({
    queryKey: ["salesAgentChatMessages", activeSessionId],
    enabled: Boolean(activeSessionId),
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_chat_message")
        .select("*")
        .eq("sessionId", activeSessionId!)
        .order("createdAt", { ascending: true });

      if (error) throw new Error(error.message);
      return (data ?? []) as AgentChatMessage[];
    },
  });

  useEffect(() => {
    if (!messagesQuery.data || isChatBusy) return;
    if (activeSessionId && skipHydrationSessionIdRef.current === activeSessionId) return;
    setMessages(messagesQuery.data.map(mapStoredMessage));
  }, [activeSessionId, isChatBusy, messagesQuery.data, setMessages]);

  const skillsQuery = useQuery({
    queryKey: skillsQueryKey,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_skill")
        .select("*")
        .eq("agentType", "sales_agent")
        .eq("kind", "skill")
        .order("sortOrder", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw new Error(error.message);
      return (data ?? []) as AgentSkill[];
    },
  });

  const saveSkillMutation = useMutation({
    mutationFn: async (values: SkillFormValues) => {
      const payload = {
        name: values.name,
        slug: values.slug || createSlug(values.name),
        description: values.description ?? "",
        content: values.content ?? "",
        enabled: values.enabled,
        sortOrder: Number(values.sortOrder ?? 0),
        updatedAt: new Date().toISOString(),
      };

      if (activeSkill) {
        const { error } = await supabase.from("agent_skill").update(payload).eq("id", activeSkill.id);
        if (error) throw new Error(error.message);
        return;
      }

      const { error } = await supabase.from("agent_skill").insert(payload);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      setEditorOpen(false);
      setActiveSkill(null);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: skillsQueryKey });
      void message.success("Skill zapisany");
    },
  });

  const toggleSkillMutation = useMutation({
    mutationFn: async ({ skill, enabled }: { skill: AgentSkill; enabled: boolean }) => {
      const { error } = await supabase
        .from("agent_skill")
        .update({ enabled, updatedAt: new Date().toISOString() })
        .eq("id", skill.id);

      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: skillsQueryKey });
    },
  });

  const deleteSkillMutation = useMutation({
    mutationFn: async (skill: AgentSkill) => {
      const { error } = await supabase.from("agent_skill").delete().eq("id", skill.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: skillsQueryKey });
      void message.success("Skill usunięty");
    },
  });

  const deleteSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase.from("agent_chat_session").delete().eq("id", sessionId);
      if (error) throw new Error(error.message);
    },
    onSuccess: async (_, sessionId) => {
      if (sessionId === activeSessionIdRef.current) {
        activeSessionIdRef.current = null;
        skipHydrationSessionIdRef.current = null;
        void setActiveSessionId(null);
        setMessages([]);
      }

      await queryClient.invalidateQueries({ queryKey: sessionsQueryKey });
      void message.success("Rozmowa usunięta");
    },
  });

  const sessions = sessionsQuery.data ?? [];
  const skills = skillsQuery.data ?? [];
  const enabledSkillsCount = useMemo(() => skills.filter((skill) => skill.enabled).length, [skills]);
  const shouldShowMessagesLoader = Boolean(
    messagesQuery.isLoading && activeSessionId && skipHydrationSessionIdRef.current !== activeSessionId,
  );
  const activeSessionTitle = sessions.find((session) => session.id === activeSessionId)?.title || "Nowy czat";

  const openNewSkill = () => {
    setActiveSkill(null);
    form.setFieldsValue({
      name: "",
      slug: "",
      description: "",
      content: defaultSkillContent,
      enabled: true,
      sortOrder: skills.length * 10 + 10,
    });
    setEditorOpen(true);
  };

  const openEditSkill = (skill: AgentSkill) => {
    setActiveSkill(skill);
    form.setFieldsValue({
      name: skill.name,
      slug: skill.slug,
      description: skill.description,
      content: skill.content,
      enabled: skill.enabled,
      sortOrder: skill.sortOrder,
    });
    setEditorOpen(true);
  };

  const selectSession = (sessionId: string) => {
    skipHydrationSessionIdRef.current = null;
    void setActiveSessionId(sessionId);
    setMessages([]);
    setAgentPanelOpen(false);
  };

  const startNewChat = () => {
    if (isChatBusy) return;
    setSelectedHistoryOwnerId(userId);
    void setActiveSessionId(null);
    activeSessionIdRef.current = null;
    skipHydrationSessionIdRef.current = null;
    setMessages([]);
    setAgentPanelOpen(false);
  };

  const submitMessage = async (text: string, files: PromptInputFile[] = []) => {
    const trimmed = text.trim();
    if ((!trimmed && files.length === 0) || isChatBusy || !isViewingOwnChats) return;
    clearError();
    const hasVoiceAttachment = files.some((file) => file.mediaType?.startsWith("audio/"));
    const messageText = trimmed || (hasVoiceAttachment ? "🎤 Wiadomość głosowa" : "Przeanalizuj załącznik.");
    const optimisticSessionId = activeSessionIdRef.current ? null : crypto.randomUUID();

    try {
      if (isChatPersistenceUnavailable) {
        void sendMessage({ text: messageText, files });
        setInput("");
        return;
      }

      if (optimisticSessionId) {
        skipHydrationSessionIdRef.current = optimisticSessionId;
        activeSessionIdRef.current = optimisticSessionId;
        void setActiveSessionId(optimisticSessionId);
      }

      const session = await ensureSession(messageText, {
        optimistic: Boolean(optimisticSessionId),
        sessionId: optimisticSessionId ?? undefined,
      });
      skipHydrationSessionIdRef.current = session.id;
      const userMessage: UIMessage = {
        id: crypto.randomUUID(),
        role: "user",
        parts: [...files.map(({ id: _id, ...file }) => file), { type: "text", text: messageText }],
      };

      await persistMessage(session.id, userMessage);
      await queryClient.invalidateQueries({ queryKey: sessionsQueryKey });
      void sendMessage(userMessage, { body: { sessionId: session.id } });
      setInput("");
    } catch (error) {
      if (optimisticSessionId && activeSessionIdRef.current === optimisticSessionId) {
        activeSessionIdRef.current = null;
        skipHydrationSessionIdRef.current = null;
        void setActiveSessionId(null);
        setMessages([]);
      }

      void message.error(error instanceof Error ? error.message : "Nie udało się wysłać wiadomości");
    }
  };

  const copyMessage = async (chatMessage: UIMessage) => {
    const text = getMessageText(chatMessage);
    if (!text) return;

    try {
      await copyTextToClipboard(text);
      void message.success("Skopiowano odpowiedź");
    } catch {
      void message.error("Nie udało się skopiować odpowiedzi");
    }
  };

  const retryLastResponse = () => {
    if (isChatBusy) return;
    clearError();
    void regenerate({ body: { sessionId: activeSessionIdRef.current ?? undefined } });
  };

  const markdownValue = Form.useWatch("content", form);

  const panel = (
    <SalesAgentPanel
      activeSessionId={activeSessionId}
      canInspectSalesRepChats={canInspectSalesRepChats}
      canManageSkills={canManageSkills}
      deleteSessionLoading={deleteSessionMutation.isLoading}
      deleteSkillLoading={deleteSkillMutation.isLoading}
      enabledSkillsCount={enabledSkillsCount}
      historyOwnerId={historyOwnerId}
      isChatBusy={isChatBusy}
      isViewingOwnChats={isViewingOwnChats}
      onDeleteSession={(sessionId) => deleteSessionMutation.mutate(sessionId)}
      onDeleteSkill={(skill) => deleteSkillMutation.mutate(skill)}
      onEditSkill={openEditSkill}
      onNewChat={startNewChat}
      onNewSkill={openNewSkill}
      onSelectHistoryOwner={setSelectedHistoryOwnerId}
      onSelectSession={selectSession}
      onToggleSkill={(skill, enabled) => toggleSkillMutation.mutate({ skill, enabled })}
      sellers={sellersQuery.data ?? []}
      sellersLoading={sellersQuery.isLoading}
      sessions={sessions}
      sessionsError={sessionsQuery.isError ? (sessionsQuery.error as Error) : null}
      skills={skills}
      skillsError={skillsQuery.isError ? (skillsQuery.error as Error) : null}
      toggleSkillLoading={toggleSkillMutation.isLoading}
      userId={userId}
    />
  );

  return (
    <div className="grid h-[calc(100vh-184px)] min-h-[680px] grid-cols-1 gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
      <Card
        className="hidden h-full min-h-0 overflow-hidden rounded-lg border border-gray-200 shadow-sm xl:block"
        styles={{ body: { height: "100%", minHeight: 0, overflow: "hidden", padding: 0 } }}
      >
        {panel}
      </Card>

      <SalesAgentChatCard
        activeSessionTitle={activeSessionTitle}
        chatError={chatError}
        chatMessages={chatMessages}
        chatStatus={chatStatus}
        enabledSkillsCount={enabledSkillsCount}
        input={input}
        isChatBusy={isChatBusy}
        isViewingOwnChats={isViewingOwnChats}
        onClearError={clearError}
        onCopyMessage={(chatMessage) => void copyMessage(chatMessage)}
        onModelChange={(modelId) => void setModelQueryState(modelId)}
        onOpenPanel={() => setAgentPanelOpen(true)}
        onRetryLastResponse={retryLastResponse}
        onStop={stopChat}
        onSubmitMessage={(text, files) => void submitMessage(text, files)}
        promptTextareaRef={promptTextareaRef}
        selectedModelId={selectedModelId}
        setInput={setInput}
        shouldShowMessagesLoader={shouldShowMessagesLoader}
      />

      <Drawer
        title="Panel agenta"
        open={agentPanelOpen}
        onClose={() => setAgentPanelOpen(false)}
        width={460}
        className="xl:hidden"
        styles={{ body: { padding: 0 } }}
      >
        {panel}
      </Drawer>

      <SkillEditorDrawer
        activeSkill={activeSkill}
        form={form}
        markdownValue={markdownValue}
        onClose={() => setEditorOpen(false)}
        onSubmit={(values) => saveSkillMutation.mutate(values)}
        open={editorOpen}
        saving={saveSkillMutation.isLoading}
      />
    </div>
  );
};
